import anchorCornerRaw from "../icons/anchor-corner.svg?raw";
import anchorCurvedRaw from "../icons/anchor-curved.svg?raw";
import anchorSmoothRaw from "../icons/anchor-smooth.svg?raw";
import ungroupRaw from "../icons/ungroup.svg?raw";
import { stripIconSvg } from "../icons/stripIconSvg";

// The panel's own glyphs (#216). Built on demand from TypeScript, so they
// read the files through `?raw` rather than an index.html marker.
const UNGROUP_ICON = stripIconSvg(ungroupRaw);
const ANCHOR_CORNER_ICON = stripIconSvg(anchorCornerRaw);
const ANCHOR_SMOOTH_ICON = stripIconSvg(anchorSmoothRaw);
const ANCHOR_CURVED_ICON = stripIconSvg(anchorCurvedRaw);

/**
 * ConstructionLinePanelSection — the construction-line properties cluster
 * extracted from PropertiesPanel (#867, epic #828 slice PP-S2). PropertiesPanel
 * OWNS a ConstructionLinePanelSection instance and DRIVES it by composition (it
 * is NOT a base class), mirroring the StitchPanelSection precedent (PP-S1) and
 * the Scene renderers: the section holds the construction-line / construction-
 * group properties BUILDERS (the per-line panel and its dimension / rotation /
 * segment-list / constraints sub-builders, the group panel, and the anchor
 * indicator + bezier-cycle helpers), while the `render()` dispatch that decides
 * WHEN to call them stays on PropertiesPanel.
 *
 * Everything the builders read from the owning panel (the DOM append target, the
 * editor context, the full panel re-render, the shared connection-row builder,
 * the active Construction sub-mode + edit-action, the constraint-picker bridge,
 * and the live-drag setter fields the tool pushes per-frame updates into) is
 * injected through the `ConstructionLinePanelHost` interface. The shared form
 * builders (`textField` / `lengthField` / `checkbox` / `numberField` /
 * `slotWidthField` / `scrubberInputField` / `presetAngleField`) stay single-
 * definition on PropertiesPanel and are imported here. Behaviour is identical to
 * the in-panel original — this is a pure move + host rewiring.
 */
import type { EditorContext } from "../tools/EditorContext";
import {
  descendantConstructionGroupIds,
  descendantConstructionLineIds,
  projectConstructionLines,
  type ConstructionGroup,
  type ConstructionLine,
} from "../model/Project";
import type {
  ConstructionSubMode,
  ConstructionEditAction,
} from "../tools/ConstructionTool";
import { DEFAULT_FREEHAND_SMOOTHNESS } from "../tools/ConstructionTool";
import {
  smoothnessFromPercent,
  smoothnessPercent,
} from "../model/freehandSmoothing";
import { cycleAnchorBezier } from "../model/constructionAnchors";
import {
  parseSubpathToSegmentList,
  segmentListToPathData,
} from "../model/constructionPathData";
import {
  modifierFor,
  dropConstructionPrimitive,
  rebuildPrimitivePath,
} from "../model/constructionLineNormalize";
import { deleteHandle as pathDeleteHandle } from "../tools/pathDeletion";
import {
  getUnrotatedBounds,
  normalizeRotation,
  rotateConstructionLine,
  rotatePathData,
} from "../model/constructionRotation";
import {
  allowedRefKindsForConstraint,
  requiredRefsForConstraint,
} from "../model/constraints";
import {
  findBrokenCoincidentConstraints,
  propagateCoincidentAfterLineMove,
} from "../model/constraintEnforcement";
import { constraintIcon } from "../icons/constraintIcons";
import {
  CONNECTIONS,
  type ConnectionRowSpec,
  routeMirrorRotationToSource,
} from "../model/connections";
import { isMirrorDerived } from "../model/constructionMirror";
import {
  sameOffsetSegment,
  trimOffsetToIncludedSegments,
} from "../model/constructionOffset";
import { repairOutlinesTracingLine } from "../model/constructionOutline";
import { setConstructionGeometry } from "../model/constructionRounding";
import { withSnappedPartnersFollowing } from "../model/constructionLineNormalize";
import { showCanvasToast } from "../canvasToast";
import { renameConstructionGroup } from "../model/projectOps";
import { ungroupConstructionSelection } from "../model/constructionGroupActions";
import { type ConstraintPickerProvider } from "./PropertiesPanel";
import {
  textField,
  lengthField,
  checkbox,
  numberField,
  slotWidthField,
  scrubberInputField,
  presetAngleField,
  fieldNote,
  panelSection,
  RESMOOTH_DISCARDS_EDITS_NOTE,
} from "./panelFieldBuilders";
import paper from "paper";

/** Resize a pill's axis (the distance between its two endpoints) to
 *  `newAxis` while keeping the centre and orientation intact. Used
 *  when the user edits the pill's "Width" or "Length" input — those
 *  controls map onto the overall capsule length, which combines axis
 *  + width, so we have to recompute the endpoints. */
function scaleAxis(
  prim: Extract<NonNullable<ConstructionLine["primitive"]>, { kind: "pill" }>,
  newAxis: number,
): Extract<NonNullable<ConstructionLine["primitive"]>, { kind: "pill" }> {
  const cx = (prim.x1 + prim.x2) / 2;
  const cy = (prim.y1 + prim.y2) / 2;
  const oldAxis = Math.hypot(prim.x2 - prim.x1, prim.y2 - prim.y1);
  // Direction defaults to horizontal when the axis collapsed to a
  // point — keeps the capsule oriented sensibly even from a pure
  // click-without-drag start.
  const dx = oldAxis > 0 ? (prim.x2 - prim.x1) / oldAxis : 1;
  const dy = oldAxis > 0 ? (prim.y2 - prim.y1) / oldAxis : 0;
  const half = newAxis / 2;
  return {
    ...prim,
    x1: cx - dx * half,
    y1: cy - dy * half,
    x2: cx + dx * half,
    y2: cy + dy * half,
  };
}

/** The shared PropertiesPanel state + services the construction-line builders
 *  reach back into. The DOM append target, the editor context, the full panel
 *  re-render, the shared `buildConnectionRow` (non-construction code uses it
 *  too), the active Construction sub-mode / edit-action, the constraint-picker
 *  bridge, and the per-frame live-drag setter fields (which `render()` clears on
 *  every rebuild) all stay owned by the panel. Implemented by PropertiesPanel. */
export interface ConstructionLinePanelHost {
  /** The panel's root append target — every construction builder appends here. */
  getRoot(): HTMLElement;
  /** The shared editor context (history / commit / scene / selection). */
  getCtx(): EditorContext;
  /** Rebuild the entire properties panel DOM for the current selection — the
   *  segment-list dot clicks re-render to reflect the new anchor state. */
  render(): void;
  /** One Connections-section row (shared with the stitch / trace panels). */
  buildConnectionRow(spec: ConnectionRowSpec): HTMLLIElement;
  /** The active Construction sub-mode (Draw / Select / …), driven from app.ts;
   *  gates the rotation scrubber (Select only). */
  getConstructionSubMode(): ConstructionSubMode;
  /** The "add constraint" picker bridge, wired once at app startup. */
  getConstraintPickerProvider(): ConstraintPickerProvider | null;
  /** The edit action the segment-list dot click should honour (delete vs
   *  cycle) — mirrors the Construction tool's editAction with the Delete
   *  sub-mode override. */
  getEffectiveEditAction(): ConstructionEditAction;
  /** Live-drag setter fields: the Construction tool pushes per-frame geometry
   *  updates into the currently-rendered inputs through these. The panel owns
   *  them because `render()` clears every one on each rebuild. */
  setOffsetLineDistanceSetLive(fn: ((mm: number) => void) | null): void;
  setActiveLinePrimitiveLengthSetLive(fn: ((mm: number) => void) | null): void;
  setActiveLinePrimitiveAngleSetLive(fn: ((deg: number) => void) | null): void;
  setRotationLiveUnsubscribe(fn: (() => void) | null): void;
}

export class ConstructionLinePanelSection {
  constructor(private host: ConstructionLinePanelHost) {}

  /** The editor context is owned by PropertiesPanel and reached through the
   *  host; the construction builders read history / commit / scene / selection
   *  off it. */
  private get ctx(): EditorContext {
    return this.host.getCtx();
  }
  /** The panel's DOM append target, reached through the host. */
  private get root(): HTMLElement {
    return this.host.getRoot();
  }
  /** The active Construction sub-mode, owned by the panel and driven from
   *  app.ts; read here to gate the rotation scrubber. */
  private get constructionSubMode(): ConstructionSubMode {
    return this.host.getConstructionSubMode();
  }
  /** The "add constraint" picker bridge, owned by the panel. */
  private get constraintPickerProvider(): ConstraintPickerProvider | null {
    return this.host.getConstraintPickerProvider();
  }
  /** Thin delegators so the moved bodies keep calling `this.render()` /
   *  `this.buildConnectionRow(...)` / `this.effectiveEditAction()` unchanged;
   *  the real implementations stay single-definition on the panel. */
  private render(): void {
    this.host.render();
  }
  private buildConnectionRow(spec: ConnectionRowSpec): HTMLLIElement {
    return this.host.buildConnectionRow(spec);
  }
  private effectiveEditAction(): ConstructionEditAction {
    return this.host.getEffectiveEditAction();
  }
  /** The live-drag setter fields live on the panel (its `render()` clears them
   *  each rebuild); the section writes them through the host so the moved bodies
   *  stay byte-identical. Set-only — nothing here reads them back. */
  private set offsetLineDistanceSetLive(fn: ((mm: number) => void) | null) {
    this.host.setOffsetLineDistanceSetLive(fn);
  }
  private set activeLinePrimitiveLengthSetLive(
    fn: ((mm: number) => void) | null,
  ) {
    this.host.setActiveLinePrimitiveLengthSetLive(fn);
  }
  private set activeLinePrimitiveAngleSetLive(
    fn: ((deg: number) => void) | null,
  ) {
    this.host.setActiveLinePrimitiveAngleSetLive(fn);
  }
  private set rotationLiveUnsubscribe(fn: (() => void) | null) {
    this.host.setRotationLiveUnsubscribe(fn);
  }

  /** Renders the construction-group panel when one or more groups
   *  are in the entity selection. Single-group selection gets a
   *  fully editable panel (header, name field, member summary,
   *  ungroup action); multi-group selection collapses to a brief
   *  summary block since per-group rename / actions would be
   *  ambiguous. The Properties panel takes precedence
   *  over the lead line's panel because a group selection means
   *  the user is acting on the group as an object — surfacing one
   *  member's panel underneath would invite editing the wrong
   *  thing. */
  public appendConstructionGroupPanel(groups: ConstructionGroup[]) {
    // Filter to the **top-most** selected groups: drop any group that
    // is a descendant of another group in the same selection. The
    // auto-promotion in `Scene.setSelection` adds every group whose
    // descendant lines are all selected — so clicking a "group of
    // groups" header (which selects every descendant line) sweeps
    // every nested subgroup into `constructionGroupIds`. Without this
    // filter the panel would render the generic "N construction
    // groups selected" block instead of the parent group's editable
    // panel, hiding the rename / Ungroup action and member counts
    // the user expects when they single-select a parent group.
    const project = this.ctx.history.state;
    const selectedIds = new Set(groups.map((g) => g.id));
    const descendantsOfSelected = new Set<string>();
    for (const g of groups) {
      for (const gid of descendantConstructionGroupIds(project, g.id)) {
        if (gid !== g.id) descendantsOfSelected.add(gid);
      }
    }
    const topMost = groups.filter(
      (g) => !descendantsOfSelected.has(g.id) && selectedIds.has(g.id),
    );
    if (topMost.length > 1) {
      const wrap = document.createElement("div");
      wrap.className = "block";
      const header = document.createElement("h4");
      header.textContent = `${topMost.length} construction groups selected`;
      wrap.appendChild(header);
      const summary = document.createElement("div");
      summary.style.color = "var(--text-dim)";
      summary.style.fontSize = "11px";
      const totalLines = topMost.reduce(
        (sum, g) =>
          sum +
          descendantConstructionLineIds(this.ctx.history.state, g.id).length,
        0,
      );
      summary.textContent =
        `${totalLines} member line${totalLines === 1 ? "" : "s"}` +
        ` across ${topMost.length} groups`;
      wrap.appendChild(summary);
      this.root.appendChild(wrap);
      return;
    }
    const group = topMost[0]!;
    const descendantLineIds = descendantConstructionLineIds(project, group.id);
    // Count every nested subgroup transitively, not just direct
    // children — keeps the summary consistent with the "Delete"
    // dialog (which wipes the whole subtree) and matches the
    // user-visible count semantic ("how many objects are inside").
    const descendantGroupCount =
      descendantConstructionGroupIds(project, group.id).length - 1;
    const wrap = document.createElement("div");
    wrap.className = "block";
    const header = document.createElement("h4");
    header.textContent = "Group";
    wrap.appendChild(header);
    this.root.appendChild(wrap);
    this.root.appendChild(
      textField("Name", group.name, (newName) => {
        const id = group.id;
        this.ctx.commit("Rename construction group", (proj) =>
          renameConstructionGroup(proj, id, newName),
        );
      }),
    );
    const summary = document.createElement("div");
    summary.style.color = "var(--text-dim)";
    summary.style.fontSize = "11px";
    summary.style.marginTop = "4px";
    const lineLabel =
      `${descendantLineIds.length} ` +
      `line${descendantLineIds.length === 1 ? "" : "s"}`;
    const groupLabel =
      descendantGroupCount === 0
        ? ""
        : `, ${descendantGroupCount} nested group${descendantGroupCount === 1 ? "" : "s"}`;
    summary.textContent = `${lineLabel}${groupLabel}`;
    this.root.appendChild(summary);

    const actions = document.createElement("div");
    actions.style.marginTop = "8px";
    actions.style.display = "flex";
    actions.style.gap = "6px";
    const ungroupBtn = document.createElement("button");
    ungroupBtn.type = "button";
    ungroupBtn.title = "Ungroup selected items (Cmd/Ctrl+Shift+G)";
    // Two squares moving outward — same glyph the layers-header /
    // toolbar Ungroup button uses.
    ungroupBtn.classList.add("panel-ungroup-btn");
    ungroupBtn.innerHTML = UNGROUP_ICON + "<span>Ungroup</span>";
    ungroupBtn.addEventListener("click", () => {
      const id = group.id;
      ungroupConstructionSelection(this.ctx);
      // Drop the now-stale group entity from the selection so the
      // panel re-renders to the line panel for the lead (or nothing).
      const sel = this.ctx.scene.getSelection();
      this.ctx.setSelection({
        pieceId: null,
        decorationId: null,
        constructionIds: sel.constructionIds ?? [],
        constructionGroupIds: (sel.constructionGroupIds ?? []).filter(
          (gid) => gid !== id,
        ),
      });
    });
    actions.appendChild(ungroupBtn);
    this.root.appendChild(actions);
  }

  /** Renders the construction-line panel; gated on a single line
   *  being the active selection. Name field renders for every line;
   *  the dimension inputs are only shown for lines that still carry
   *  their primitive metadata (rect / ellipse / pill that hasn't
   *  been edited away from the parametric shape). */
  public appendConstructionLinePanel(line: ConstructionLine) {
    const prim = line.primitive;
    const wrap = document.createElement("div");
    wrap.className = "block";
    const header = document.createElement("h4");
    // Header label hierarchy: a parametric primitive wins (e.g. "Square"),
    // otherwise fall back to the authored kind ("Polyline" / "Polygon" /
    // "Curve" for freehand). Anything else (legacy lines without either)
    // gets the generic label.
    header.textContent = prim
      ? modifierFor(prim)
      : line.kind === "polyline"
        ? "Polyline"
        : line.kind === "polygon"
          ? "Polygon"
          : line.kind === "freehand"
            ? "Curve"
            : "Construction line";
    wrap.appendChild(header);
    this.root.appendChild(wrap);

    // Name editor — always shown so the user can rename any
    // construction line, parametric or not. Saves on blur / Enter.
    const fallbackName = `Construction ${
      projectConstructionLines(this.ctx.history.state).indexOf(line) + 1
    }`;
    this.root.appendChild(
      textField("Name", line.name ?? fallbackName, (newName) => {
        const id = line.id;
        this.ctx.commit("Rename construction line", (proj) => ({
          ...proj,
          constructionLines: (proj.constructionLines ?? []).map((l) =>
            l.id === id ? { ...l, name: newName } : l,
          ),
        }));
      }),
    );

    // Smoothness slider (issue #171). Any freehand line that still carries its
    // raw pointer-sample source exposes this knob, and since #248 it keeps
    // carrying it through hand edits — so the slider survives a point drag
    // instead of retiring at the first one. The commit re-runs the derive pass
    // in `app.ts`, which re-fits the curve from the trace and therefore
    // discards those edits; the note below says so.
    if (line.kind === "freehand" && line.rawPoints) {
      const id = line.id;
      this.root.appendChild(
        numberField(
          "Smoothness (%)",
          smoothnessPercent(line.smoothness ?? DEFAULT_FREEHAND_SMOOTHNESS),
          0,
          100,
          1,
          (pct) => {
            const v = smoothnessFromPercent(pct);
            this.ctx.commit("Adjust smoothness", (proj) => ({
              ...proj,
              constructionLines: (proj.constructionLines ?? []).map((l) =>
                // Applying smoothness makes the line a derived view of the
                // trace again, so the derive pass takes over from here (#248).
                l.id === id
                  ? { ...l, smoothness: v, freehandEdited: undefined }
                  : l,
              ),
            }));
            // Re-fitting changes the anchors, and the segment list below
            // enumerates them — so it has to follow the drag or it lists
            // anchors the line no longer has.
            this.refreshConstructionSegmentList(id);
            // The caution below is about edits that no longer exist. A commit
            // doesn't rebuild the panel while a field has focus — which this
            // one does, having just been typed in — so ask for it. (A full
            // rebuild is fine HERE: it happens once, when the flag clears,
            // rather than on every tick of the drag.)
            if (line.freehandEdited) this.render();
          },
        ),
      );
      if (line.freehandEdited) {
        this.root.appendChild(fieldNote(RESMOOTH_DISCARDS_EDITS_NOTE));
      }
    }

    // Derived offset lines (issue #29): expose only the Distance
    // input + constraints section. The line's geometry is owned by
    // the source, so primitive dimensions, segment lists, and any
    // per-vertex affordances are all read-only / inapplicable here.
    if (line.offsetSource) {
      const link = line.offsetSource;
      this.root.appendChild(
        lengthField(
          "Distance",
          link.distance,
          -1000,
          1000,
          0.1,
          (mm) => {
            const id = line.id;
            this.ctx.commit("Set offset distance", (proj) => ({
              ...proj,
              constructionLines: (proj.constructionLines ?? []).map((l) =>
                l.id === id
                  ? {
                      ...l,
                      offsetSource: {
                        ...(l.offsetSource ?? {
                          sourceId: link.sourceId,
                          distance: mm,
                        }),
                        distance: mm,
                      },
                    }
                  : l,
              ),
            }));
          },
          {
            allowNegative: true,
            noSlider: true,
            liveCommit: true,
            captureSetValue: (setValue) => {
              this.offsetLineDistanceSetLive = setValue;
            },
          },
        ),
      );
      // "Round corners" toggle (issue #244 follow-up). Only meaningful
      // when the heuristic can act on it: outward (distance > 0) +
      // sharp source rectangle or polygon. Hidden otherwise so the
      // panel doesn't surface a control that has no effect (e.g.
      // ellipse / pill sources, inward offsets, rounded-rectangle
      // sources where the source's own radii drive the offset corners).
      const sources = projectConstructionLines(this.ctx.history.state);
      const sourceLine = sources.find((l) => l.id === link.sourceId);
      const sourceIsSharpRect =
        sourceLine?.primitive?.kind === "rectangle" &&
        !sourceLine.cornerRounding;
      // Polylines get the same interior-corner rounding as polygons
      // (PR #267 — endpoints stay sharp, interior vertices get arcs
      // of radius=distance). Expose the same toggle so the user can
      // opt out.
      const sourceIsPolygonOrPolyline =
        (sourceLine?.kind === "polygon" || sourceLine?.kind === "polyline") &&
        !sourceLine?.cornerRounding;
      if (
        (sourceIsSharpRect || sourceIsPolygonOrPolyline) &&
        link.distance > 0
      ) {
        // Defaults to ON: `roundedCorners` is undefined for fresh
        // offsets and on legacy projects, both of which should
        // render with rounded corners (the geometric meaning of a
        // uniform outward expansion). The flag is only stored as
        // `false` when the user explicitly opts out, so the
        // checkbox state is `roundedCorners !== false`.
        this.root.appendChild(
          checkbox("Round corners", link.roundedCorners !== false, (v) => {
            const id = line.id;
            this.ctx.commit("Toggle offset rounded corners", (proj) => ({
              ...proj,
              constructionLines: (proj.constructionLines ?? []).map((l) => {
                if (l.id !== id || !l.offsetSource) return l;
                const nextOffsetSource = { ...l.offsetSource };
                if (v) {
                  // Back to default — drop the field rather than
                  // store the redundant `true`.
                  delete nextOffsetSource.roundedCorners;
                } else {
                  nextOffsetSource.roundedCorners = false;
                }
                return { ...l, offsetSource: nextOffsetSource };
              }),
            }));
          }),
        );
      }
      if (sourceLine) {
        this.appendOffsetSegmentList(line, link, sourceLine);
      }
      this.appendConstructionConstraintsSection(line);
      return;
    }

    // Rotation scrubber (issue #67): visible while the Select
    // sub-mode is active so the move tool's properties show how the
    // line is oriented and let the user dial it back to 0 / set an
    // exact angle. Range -180..180 in 1° steps. Calls into the
    // Construction tool's `applyConstructionLineRotation` which
    // computes the delta and commits via the same path the drag
    // gesture uses.
    if (this.constructionSubMode === "select") {
      this.appendConstructionRotationField(line);
    }

    // Per-segment list for any non-primitive, non-derived line — curves
    // included (#248). The slider used to REPLACE this list on a freehand
    // line, on the reasoning that a list of pointer-sample anchors is noise
    // next to the control that generates them. That reasoning depended on the
    // trace being dropped at the first hand edit, after which the list took
    // over; now that the trace is kept for good, "replace" would mean a
    // freehand line never shows its points again. They coexist: the slider
    // rebuilds the anchors, the list addresses the ones it produced.
    if (!prim) {
      const list = this.buildConstructionSegmentList(line);
      if (list) this.root.appendChild(list);
    }

    if (!prim) {
      // No primitive metadata — render the constraints section as
      // the last thing on this panel and bail out (no dimension
      // inputs to follow).
      this.appendConstructionConstraintsSection(line);
      return;
    }

    const commit = (
      next: NonNullable<ConstructionLine["primitive"]>,
      newPathData: string,
    ) => {
      const id = line.id;
      const scope = this.ctx.scene.getPaperScope();
      this.ctx.commit("Resize construction line", (proj) => {
        const resized = (proj.constructionLines ?? []).map((l) => {
          if (l.id !== id) return l;
          // For axis-aligned primitives (rectangle / ellipse), the
          // line's `rotation` field carries any tilt that's been
          // applied since the primitive was drawn (issue #168). The
          // new `newPathData` from `rebuildPrimitivePath` is the
          // un-rotated form; re-apply the stored rotation so editing
          // width / height preserves the tilt the user set up.
          const rot = l.rotation ?? 0;
          const center = l.rotationCenter;
          const finalPathData =
            center && Math.abs(rot) > 1e-6
              ? rotatePathData(newPathData, center.x, center.y, rot, scope)
              : newPathData;
          // The rotated outline is the sharp source too: rotation stores
          // `cornerRounding.sharpPathData` in world (tilted) form, and the
          // rounding pass re-derives `pathData` from it on every commit.
          // Writing the UN-rotated form there (the #324 fix) let that pass
          // drop the tilt; writing nothing let it undo the resize (#1165).
          return setConstructionGeometry(l, finalPathData, next, scope);
        });
        // Snapped partners follow the resized shape from every anchor,
        // the center and the edge midpoints, stretching when linked at two
        // points — the same propagation the canvas resize runs, so the two
        // resize paths agree (#1166).
        return withSnappedPartnersFollowing(proj, resized, [id], scope);
      });
    };

    if (prim.kind === "line") {
      // Line primitive — derive length + visual angle from the
      // endpoints. Editing either rebuilds the line: length keeps the
      // midpoint and angle constant; angle keeps the midpoint and
      // length constant. Mirrors the slot primitive's "length stays
      // when changing one axis" semantics.
      const dx = prim.x2 - prim.x1;
      const dy = prim.y2 - prim.y1;
      const length = Math.hypot(dx, dy);
      // Visual degrees CCW from horizontal right (y-up). Project
      // space is y-down so we negate dy before atan2.
      let visualDeg = (Math.atan2(-dy, dx) * 180) / Math.PI;
      if (visualDeg < 0) visualDeg += 360;
      const buildLineFromLengthAngle = (
        newLength: number,
        newVisualDeg: number,
      ): NonNullable<ConstructionLine["primitive"]> => {
        const cx = (prim.x1 + prim.x2) / 2;
        const cy = (prim.y1 + prim.y2) / 2;
        const rad = (newVisualDeg * Math.PI) / 180;
        const dirX = Math.cos(rad);
        const dirY = -Math.sin(rad);
        const half = newLength / 2;
        return {
          kind: "line",
          x1: cx - dirX * half,
          y1: cy - dirY * half,
          x2: cx + dirX * half,
          y2: cy + dirY * half,
        };
      };
      // Per-line commit for length / angle edits: update the line's
      // pathData + primitive, then let snapped partners follow. A
      // length / angle change moves both ends (mirrored around the
      // midpoint), so partners on either end must follow; the whole-line
      // propagation seeds both, and never pulls the edited line's own
      // ends back onto a partner (#1166).
      const id = line.id;
      const linePrimitiveCommit = (
        next: NonNullable<ConstructionLine["primitive"]>,
        newPathData: string,
        label: string,
      ) => {
        const scope = this.ctx.scene.getPaperScope();
        this.ctx.commit(label, (proj) => {
          const updated = (proj.constructionLines ?? []).map((l) =>
            l.id === id
              ? setConstructionGeometry(l, newPathData, next, scope)
              : l,
          );
          return withSnappedPartnersFollowing(proj, updated, [id], scope);
        });
      };
      // Wrap both the Length and Angle fields in a marker div so
      // tests / styling can target the per-line-primitive section
      // separately from the tool-mode preset section above.
      const section = document.createElement("div");
      section.dataset.linePrimitiveSection = "true";
      let lengthSetValue: ((mm: number) => void) | null = null;
      section.appendChild(
        lengthField(
          "Length",
          length,
          0.1,
          10000,
          0.1,
          (mm) => {
            if (mm <= 0) return;
            const next = buildLineFromLengthAngle(mm, visualDeg);
            linePrimitiveCommit(
              next,
              rebuildPrimitivePath(next, this.ctx.scene.getPaperScope()),
              "Resize construction line",
            );
          },
          {
            noSlider: true,
            liveCommit: true,
            captureSetValue: (setValue) => {
              lengthSetValue = setValue;
            },
          },
        ),
      );
      const angleField = presetAngleField(visualDeg, (deg) => {
        if (deg == null) return;
        const next = buildLineFromLengthAngle(length, deg);
        linePrimitiveCommit(
          next,
          rebuildPrimitivePath(next, this.ctx.scene.getPaperScope()),
          "Rotate construction line",
        );
      });
      section.appendChild(angleField.row);
      this.root.appendChild(section);
      // Wire the canvas-drag live-update bridge. Both setters skip
      // when their input is currently focused (the user is
      // mid-typing) so a per-frame canvas update doesn't yank the
      // caret around. The lookup walks the rendered section for the
      // input by role so the test harness data attributes stay the
      // source of truth.
      const isAnyInputFocused = (within: HTMLElement): boolean => {
        const active = document.activeElement;
        return (
          active instanceof HTMLElement &&
          within.contains(active) &&
          (active.tagName === "INPUT" || active.tagName === "TEXTAREA")
        );
      };
      this.activeLinePrimitiveLengthSetLive = (mm) => {
        if (mm <= 0) return;
        // Skip the update while the user is editing either of the
        // Length cells (mm or in) — overwriting an in-flight value
        // would clobber the caret position and the partial input.
        if (isAnyInputFocused(section)) return;
        lengthSetValue?.(mm);
      };
      this.activeLinePrimitiveAngleSetLive = (deg) => {
        if (isAnyInputFocused(section)) return;
        // setValue (not setLive) — for the per-line panel the angle
        // field always carries a stored value (the line's current
        // angle), so the field's setLive helper would early-out on
        // its `stored != null` guard. setValue updates both the
        // stored value and the rendered chip highlight, which is
        // exactly the "track the live geometry" behaviour we want.
        angleField.setValue(deg);
      };
      this.appendConstructionConstraintsSection(line);
      return;
    }

    if (prim.kind === "rectangle" || prim.kind === "ellipse") {
      const widthLabel =
        prim.kind === "rectangle" && Math.abs(prim.width - prim.height) < 0.001
          ? "Side length"
          : prim.kind === "ellipse" &&
              Math.abs(prim.width - prim.height) < 0.001
            ? "Diameter"
            : "Width";
      const showHeight =
        Math.abs(prim.width - prim.height) >= 0.001 ||
        // Always offer the height field for rectangles / ellipses
        // even when currently square / circle, so the user can break
        // the equality. The merged "Side length" / "Diameter" label
        // covers the equal case; otherwise show paired W / H.
        false;

      this.root.appendChild(
        lengthField(
          showHeight ? widthLabel : widthLabel,
          prim.width,
          0.1,
          10000,
          0.1,
          (mm) => {
            if (mm <= 0) return;
            // When the two axes were equal we treat this as a single
            // "side / diameter" control that drives both axes.
            const isUnison = Math.abs(prim.width - prim.height) < 0.001;
            const next = {
              ...prim,
              width: mm,
              height: isUnison ? mm : prim.height,
            };
            commit(
              next,
              rebuildPrimitivePath(next, this.ctx.scene.getPaperScope()),
            );
          },
          { noSlider: true, liveCommit: true },
        ),
      );
      // Only show the second input when the rectangle / ellipse is
      // actually unequal — square / circle collapses into a single
      // "side length" / "diameter" control above.
      if (Math.abs(prim.width - prim.height) >= 0.001) {
        this.root.appendChild(
          lengthField(
            "Height",
            prim.height,
            0.1,
            10000,
            0.1,
            (mm) => {
              if (mm <= 0) return;
              const next = { ...prim, height: mm };
              commit(
                next,
                rebuildPrimitivePath(next, this.ctx.scene.getPaperScope()),
              );
            },
            { noSlider: true, liveCommit: true },
          ),
        );
      }
    } else {
      // Pill — derived "length" is the overall capsule length
      // (axis-distance + width); width is the short axis. Width
      // reuses the construction-tool's chip-based slot picker so
      // common pitches (3 mm, 1/4", etc.) are one tap away. The
      // chip slider would crowd the metadata column, so opt out via
      // `noSlider`.
      const axisLen = Math.hypot(prim.x2 - prim.x1, prim.y2 - prim.y1);
      const overallLength = axisLen + prim.width;
      this.root.appendChild(
        slotWidthField(
          prim.width,
          (mm) => {
            if (mm <= 0) return;
            // Keep overall length constant — shrink / grow the axis to
            // compensate for the new short-axis width.
            const newAxis = Math.max(0, overallLength - mm);
            const next = scaleAxis(prim, newAxis);
            next.width = mm;
            commit(
              next,
              rebuildPrimitivePath(next, this.ctx.scene.getPaperScope()),
            );
          },
          "Width",
        ),
      );
      this.root.appendChild(
        lengthField(
          "Length",
          overallLength,
          0.1,
          10000,
          0.1,
          (mm) => {
            if (mm <= prim.width) return;
            // Keep width constant — adjust the axis distance so
            // axis + width = newLength.
            const newAxis = Math.max(0, mm - prim.width);
            const next = scaleAxis(prim, newAxis);
            commit(
              next,
              rebuildPrimitivePath(next, this.ctx.scene.getPaperScope()),
            );
          },
          { noSlider: true, liveCommit: true },
        ),
      );
    }
    // Constraints come last on the panel — after the dimension
    // inputs — so the read-only / palette section doesn't push
    // the more frequently-used fields off-screen.
    this.appendConstructionConstraintsSection(line);
  }

  /** Rotation scrubber for the Select sub-mode (issue #67). A
   *  -180 .. 180 range pair with a 1° step; on input we compute the
   *  delta from the line's current rotation and commit one combined
   *  step. Commits live (per input event) so the canvas tracks the
   *  scrubber, but normalises to (-180, 180] so a wrap-around drag
   *  doesn't run away. */
  private appendConstructionRotationField(line: ConstructionLine) {
    const current = normalizeRotation(line.rotation ?? 0);

    const apply = (target: number) => {
      const lineId = line.id;
      const scope = this.ctx.scene.getPaperScope();
      const proj = this.ctx.history.state;
      const cur = projectConstructionLines(proj).find((l) => l.id === lineId);
      if (!cur) return;
      const t = normalizeRotation(target);
      const c = normalizeRotation(cur.rotation ?? 0);
      let delta = t - c;
      while (delta > 180) delta -= 360;
      while (delta < -180) delta += 360;
      if (Math.abs(delta) < 0.01) return;
      const bounds = getUnrotatedBounds(cur, scope);
      if (!bounds) return;
      // Honour a user-positioned pivot (issue #420) so the scrubber
      // spins about the same point as the canvas gesture / pivot
      // crosshair. With no custom pivot, keep the historical
      // behaviour (rotate about the stored rotationCenter / centre).
      const cx = cur.pivotPoint ? cur.pivotPoint.x : bounds.cx;
      const cy = cur.pivotPoint ? cur.pivotPoint.y : bounds.cy;
      // Bidirectional editing for mirror-derived lines (issue #180):
      // the scrubber on a mirror dials the mirror's effective angle,
      // but the mutation has to happen on the SOURCE — otherwise the
      // commit-time `deriveConstructionMirrors` pass clobbers any
      // direct rotation we'd apply to the mirror's pathData. Route
      // the (delta, pivot) through the chain (sign flip per line
      // reflection, pivot reflected through each axis), then rotate
      // the source root. Mirror re-derives off the rotated source
      // with `mirror.rotation = sign · source.rotation` so the
      // displayed value matches what the user typed.
      //
      // Refused when the chain hits an offset layer (no clean
      // inverse for perpendicular-distance offset math) — early
      // return leaves the source unchanged. Same contract as the
      // knob gesture in `ConstructionTool.startLineRotationDrag`.
      if (isMirrorDerived(cur)) {
        const routed = routeMirrorRotationToSource(
          proj,
          lineId,
          { x: cx, y: cy },
          scope,
        );
        if (!routed) {
          // Chain hits an offset — same refusal contract as the
          // canvas rotation gestures. Show the same hint so the
          // user knows the typed value intentionally didn't take
          // effect.
          showCanvasToast(
            "Edit the source line directly — can't route through an offset.",
          );
          return;
        }
        const sourceDelta = routed.sign * delta;
        const sourceCx = routed.sourcePivot.x;
        const sourceCy = routed.sourcePivot.y;
        const sourceId = routed.sourceRootId;
        this.ctx.commit("Rotate construction line (via mirror)", (p) =>
          withSnappedPartnersFollowing(
            p,
            projectConstructionLines(p).map((l) =>
              l.id === sourceId
                ? rotateConstructionLine(
                    l,
                    sourceDelta,
                    sourceCx,
                    sourceCy,
                    scope,
                  )
                : l,
            ),
            [sourceId],
            scope,
          ),
        );
        return;
      }
      // Snapped partners follow the rotation, stretching when linked at
      // two points — the same rule as the canvas rotation knob (#1166).
      this.ctx.commit("Rotate construction line", (p) =>
        withSnappedPartnersFollowing(
          p,
          projectConstructionLines(p).map((l) =>
            l.id === lineId
              ? rotateConstructionLine(l, delta, cx, cy, scope)
              : l,
          ),
          [lineId],
          scope,
        ),
      );
    };

    // Unified scrubber + input control (issue #528 / #529). `apply`
    // computes a delta from the committed rotation and commits, so the
    // canvas updates live as the user scrubs (live and commit both call
    // it; the release `apply` is a ~0 delta no-op).
    const { wrap, setValue } = scrubberInputField({
      label: "Rotation",
      ariaLabel: "Construction line rotation",
      min: -180,
      max: 180,
      step: 1,
      suffix: "°",
      value: Math.round(current),
      onLive: (deg) => apply(deg),
      onCommit: (deg) => apply(deg),
    });

    // Track live rotation drags on the canvas so the scrubber + number
    // mirror the in-flight angle without waiting for the commit on
    // drag end. Scene fires the listener with the absolute effective
    // angle; `null` means the drag finished and a fresh render will
    // re-read the committed `line.rotation`, so we drop the override.
    const lineId = line.id;
    this.rotationLiveUnsubscribe = this.ctx.scene.subscribeRotationLive(
      (spec) => {
        if (!spec || spec.lineId !== lineId) return;
        setValue(Math.round(spec.deg));
      },
    );

    this.root.appendChild(wrap);
  }

  /** Render the per-segment list for a polyline / polygon: one row
   *  per curve segment between consecutive anchors. Each row stacks
   *  its two end-anchor dots vertically in a single right-aligned
   *  column so that adjacent rows naturally share an anchor column —
   *  a thin "bridge" line can then be drawn between the row above's
   *  bottom dot and the row below's top dot whenever the shared
   *  anchor carries an angle / smooth lock, mirroring the canvas
   *  convention of "line through both tangent dots = locked anchor".
   *  Tapping any dot cycles the underlying anchor through the same
   *  4-state bezier cycle as the canvas smooth-toggle sub-tool. */
  private buildConstructionSegmentList(
    line: ConstructionLine,
  ): HTMLDivElement | null {
    const scope = this.ctx.scene.getPaperScope();
    let path: paper.CompoundPath;
    try {
      path = new scope.CompoundPath({ pathData: line.pathData, insert: false });
    } catch {
      return null;
    }
    const subpaths: paper.Path[] =
      path.children && path.children.length > 0
        ? (path.children as paper.Path[])
        : [path as unknown as paper.Path];
    const lockEntries = line.smoothAnchors ?? [];
    const lockMode = (
      pathIndex: number,
      segmentIndex: number,
    ): "angle" | "smooth" | null => {
      const entry = lockEntries.find(
        (a) => a.pathIndex === pathIndex && a.segmentIndex === segmentIndex,
      );
      if (!entry) return null;
      return entry.mode ?? "smooth";
    };

    const list = document.createElement("div");
    list.className = "segment-list";
    // Drives a CSS color switch — yellow dots by default, red dots
    // (and a dimmed cursor on the no-handle case) when the user is
    // in the delete sub-sub-tool.
    list.dataset.editAction = this.effectiveEditAction();

    // Track the previous row's END anchor so we can drop a bridge
    // line between rows whenever the shared anchor (this row's
    // START = previous row's END) is locked. Reset on each subpath
    // boundary since two subpaths don't actually share anchors.
    let prevEnd: {
      pathIndex: number;
      segmentIndex: number;
      locked: boolean;
      /** The previous row's END dot — referenced when this row's
       *  START dot turns out to be bridged, so we can pair the
       *  two for shared hover highlighting. */
      endDot: HTMLButtonElement;
    } | null = null;

    subpaths.forEach((sub, pathIndex) => {
      prevEnd = null;
      // For each curve in the subpath: (segment N, segment N+1).
      // Closed subpaths get their wrap-around "last → first" pair,
      // and we also drop a bridge between the very last and very
      // first row at the end so the loop visually closes.
      const segs = sub.segments;
      const pairCount = sub.closed ? segs.length : segs.length - 1;
      for (let i = 0; i < pairCount; i++) {
        const aIdx = i;
        const bIdx = (i + 1) % segs.length;
        const segA = segs[aIdx]!;
        const segB = segs[bIdx]!;
        // The handles facing this curve segment determine "is this
        // sub-segment a straight line or a curve" — segA's handleOut
        // points along the curve toward B, segB's handleIn points
        // back toward A. If both are zero the segment is straight.
        const isStraight = segA.handleOut.isZero() && segB.handleIn.isZero();

        // Bridge between previous row's END dot and this row's
        // START dot when they're the same anchor and that anchor is
        // locked. Drawn as a CSS pseudo-element on the START dot so
        // it's absolutely positioned (no flow space, no widening of
        // the row gap) and overlays both dots — visually extending
        // the inner-connector through the shared locked anchor.
        const bridgeAbove =
          prevEnd !== null &&
          prevEnd.pathIndex === pathIndex &&
          prevEnd.segmentIndex === aIdx &&
          prevEnd.locked;

        const row = document.createElement("div");
        row.className = "segment-row";
        // Hover the row → highlight the matching curve on the
        // canvas with a marching-ants overlay so the user can find
        // each segment at a glance. Both the enter and leave
        // handlers are cheap (one Scene method call apiece).
        const constructionId = line.id;
        const hoverPath = pathIndex;
        const hoverSeg = aIdx;
        row.addEventListener("mouseenter", () => {
          this.ctx.scene.setConstructionSegmentHover({
            constructionId,
            pathIndex: hoverPath,
            segmentIndex: hoverSeg,
          });
        });
        row.addEventListener("mouseleave", () => {
          this.ctx.scene.setConstructionSegmentHover(null);
        });

        const label = document.createElement("span");
        label.className = "segment-label";
        label.textContent = isStraight ? "Straight line" : "Bezier curve";
        row.appendChild(label);

        const indicators = document.createElement("div");
        indicators.className = "segment-indicators";
        // Each end's icon reflects the anchor's current bezier state
        // (small dot / hollow circle / filled circle). Click cycles
        // the anchor through all 4 states (same as the canvas
        // smooth-toggle); the OTHER segment row that shares this
        // anchor refreshes on the next render so its indicator stays
        // in sync without any extra plumbing.
        // The start dot represents anchor `aIdx` from this
        // segment's point of view — i.e., the side facing this
        // segment is `handleOut`. The end dot is `bIdx` with its
        // `handleIn`. Per-side classification means an anchor
        // with one zero handle and one non-zero handle gets
        // different glyphs on the two sides — matches the user's
        // expectation after a single-handle delete.
        const startDot = this.makeAnchorIndicator(
          line.id,
          pathIndex,
          aIdx,
          "out",
          this.classifyAnchorState(segA, lockMode(pathIndex, aIdx), "out"),
          aIdx,
        );
        startDot.classList.add("segment-dot-start");
        if (bridgeAbove) {
          startDot.classList.add("segment-dot-bridged-above");
          // Real DOM element for the bridge (instead of a pseudo)
          // so it can have its own mouseenter — hovering the
          // bridge specifically lights up BOTH sides of the
          // shared anchor on the canvas, since the bridge
          // represents the locked link between them.
          const bridgeEl = document.createElement("span");
          bridgeEl.className = "segment-bridge";
          bridgeEl.setAttribute("aria-hidden", "true");
          bridgeEl.addEventListener("mouseenter", () => {
            this.ctx.scene.setConstructionSegmentHover(null);
            this.ctx.scene.setConstructionPanelHover({
              kind: "both-handles",
              constructionId: line.id,
              pathIndex,
              segmentIndex: aIdx,
            });
          });
          bridgeEl.addEventListener("mouseleave", () => {
            // Cursor is still inside the dot button (the bridge
            // is a child) — restore the dot's single-side hover
            // so moving from bridge back into the dot reads as a
            // continuation rather than a flicker.
            this.ctx.scene.setConstructionPanelHover({
              kind: "handle",
              constructionId: line.id,
              pathIndex,
              segmentIndex: aIdx,
              side: "out",
            });
          });
          startDot.appendChild(bridgeEl);
        }
        // Pair the bridged START dot with the previous row's END
        // dot — they represent the SAME (locked) anchor, and the
        // user expects a hover on either one to light up both
        // along with the bridge between them.
        if (bridgeAbove && prevEnd) {
          const partnerEnd = prevEnd.endDot;
          const onEnter = () => {
            startDot.classList.add("segment-dot-paired-hover");
            partnerEnd.classList.add("segment-dot-paired-hover");
          };
          const onLeave = () => {
            startDot.classList.remove("segment-dot-paired-hover");
            partnerEnd.classList.remove("segment-dot-paired-hover");
          };
          startDot.addEventListener("mouseenter", onEnter);
          startDot.addEventListener("mouseleave", onLeave);
          partnerEnd.addEventListener("mouseenter", onEnter);
          partnerEnd.addEventListener("mouseleave", onLeave);
        }
        indicators.appendChild(startDot);
        const conn = document.createElement("div");
        conn.className = "segment-connector";
        indicators.appendChild(conn);
        const endDot = this.makeAnchorIndicator(
          line.id,
          pathIndex,
          bIdx,
          "in",
          this.classifyAnchorState(segB, lockMode(pathIndex, bIdx), "in"),
          aIdx,
        );
        endDot.classList.add("segment-dot-end");
        indicators.appendChild(endDot);
        row.appendChild(indicators);
        list.appendChild(row);

        prevEnd = {
          pathIndex,
          segmentIndex: bIdx,
          locked: lockMode(pathIndex, bIdx) !== null,
          endDot,
        };
      }
    });
    path.remove();

    return list.childElementCount > 0 ? list : null;
  }

  /** Rebuild the segment list IN PLACE against the line's current geometry.
   *
   *  The Smoothness slider commits on every drag tick, and the anchors it is
   *  re-fitting are exactly what this list enumerates — so a list left alone
   *  goes stale the moment the slider moves, listing anchors the line no
   *  longer has. A full panel rebuild is not an option here: it would replace
   *  the input the user is currently dragging. Swapping this one element keeps
   *  the drag intact. */
  private refreshConstructionSegmentList(lineId: string) {
    const existing = this.root.querySelector(".segment-list");
    if (!existing) return;
    const line = projectConstructionLines(this.ctx.history.state).find(
      (l) => l.id === lineId,
    );
    if (!line) return;
    const next = this.buildConstructionSegmentList(line);
    if (next) existing.replaceWith(next);
    else existing.remove();
  }

  /** Which edges of its source an offset line follows (issue #167).
   *  An offset's own geometry has no editable segments — it is
   *  re-derived every commit — so the list is of the SOURCE's edges,
   *  each with a checkbox saying whether the offset runs alongside it.
   *  Unticking one is the panel-side twin of tapping that stretch with
   *  the Delete sub-tool (#116), and is the only way to put a stretch
   *  back: once removed it is not on the canvas to tap.
   *
   *  Hovering a row lights up the matching edge of the SOURCE, which is
   *  what the row names — the offset's own stretch may not be there to
   *  point at.
   *
   *  Hidden when there is nothing to choose between: a single-edge
   *  offset (#681) follows one edge by definition, and a one-edge
   *  source offers no choice either. The last ticked box is disabled
   *  rather than allowed to empty the line — a checkbox that deletes
   *  the thing it belongs to is a nasty surprise, and Delete already
   *  removes the whole offset. */
  private appendOffsetSegmentList(
    line: ConstructionLine,
    link: NonNullable<ConstructionLine["offsetSource"]>,
    source: ConstructionLine,
  ): void {
    if (link.segment) return;
    const scope = this.ctx.scene.getPaperScope();
    let path: paper.CompoundPath;
    try {
      path = new scope.CompoundPath({
        pathData: source.pathData,
        insert: false,
      });
    } catch {
      return;
    }
    const subpaths: paper.Path[] =
      path.children && path.children.length > 0
        ? (path.children as paper.Path[])
        : [path as unknown as paper.Path];
    const edges: Array<{
      pathIndex: number;
      curveIndex: number;
      straight: boolean;
    }> = [];
    subpaths.forEach((sub, pathIndex) => {
      (sub.curves ?? []).forEach((curve, curveIndex) => {
        edges.push({ pathIndex, curveIndex, straight: !curve.hasHandles() });
      });
    });
    path.remove();
    if (edges.length < 2) return;

    const excluded = link.excludedSegments ?? [];
    const follows = (e: { pathIndex: number; curveIndex: number }) =>
      !excluded.some((x) => sameOffsetSegment(x, e));
    const followedCount = edges.filter(follows).length;

    const section = panelSection("Follows edges");

    const list = document.createElement("div");
    list.className = "segment-list";
    const sourceId = source.id;
    const lineId = line.id;
    edges.forEach((edge, i) => {
      // The row IS the <label>, so the whole strip is a hit target and
      // it inherits `.segment-row`'s flex + hover styling unchanged —
      // text left, control right, like every other row in this panel.
      const row = document.createElement("label");
      row.className = "segment-row";
      row.addEventListener("mouseenter", () => {
        this.ctx.scene.setConstructionSegmentHover({
          constructionId: sourceId,
          pathIndex: edge.pathIndex,
          segmentIndex: edge.curveIndex,
        });
      });
      row.addEventListener("mouseleave", () => {
        this.ctx.scene.setConstructionSegmentHover(null);
      });

      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = follows(edge);
      // Numbered because every row otherwise reads the same, and an
      // offset with six straight edges gives the user no way to say
      // which one they mean.
      const text = document.createElement("span");
      text.className = "segment-label";
      text.textContent = `${i + 1}. ${
        edge.straight ? "Straight line" : "Bezier curve"
      }`;
      if (cb.checked && followedCount === 1) {
        cb.disabled = true;
        row.title = "An offset has to follow at least one edge.";
      }
      cb.addEventListener("change", () => {
        const wanted = cb.checked;
        const target = {
          pathIndex: edge.pathIndex,
          curveIndex: edge.curveIndex,
        };
        const scope = this.ctx.scene.getPaperScope();
        this.ctx.commit(
          wanted ? "Follow source edge" : "Skip source edge",
          (proj) => {
            const lines = (proj.constructionLines ?? []).map((l) => {
              if (l.id !== lineId || !l.offsetSource) return l;
              const next = (l.offsetSource.excludedSegments ?? []).filter(
                (x) => !sameOffsetSegment(x, target),
              );
              if (!wanted) next.push(target);
              const offsetSource = { ...l.offsetSource };
              // Drop the field rather than store an empty list — an
              // offset that follows everything is the default shape,
              // and the deriver's untrimmed fast path keys off it.
              if (next.length > 0) offsetSource.excludedSegments = next;
              else delete offsetSource.excludedSegments;
              // Re-derive the geometry here rather than waiting for the
              // derive pass at the end of the commit, so the detach below
              // sees the shape that survived and can trim a traced seam to
              // it. The deriver recomputes the same thing a moment later.
              const src = (proj.constructionLines ?? []).find(
                (o) => o.id === offsetSource.sourceId,
              );
              const derived = src
                ? trimOffsetToIncludedSegments(
                    l.pathData,
                    src.pathData,
                    next,
                    scope,
                  )
                : null;
              return derived
                ? { ...l, pathData: derived, offsetSource }
                : { ...l, offsetSource };
            });
            // Changing which edges the offset follows renumbers the anchors
            // anything TRACING it addressed its segments by, so re-point those
            // traces at what the offset still draws — the same thing the
            // Delete sub-tool's tap does (#116, repaired in place per #1029).
            return repairOutlinesTracingLine(
              proj,
              { ...proj, constructionLines: lines },
              lineId,
              scope,
            );
          },
        );
        // A commit doesn't rebuild the panel on its own, and this list
        // changes shape with the model: the last ticked box has to
        // become disabled, and un-disable again once a second edge is
        // back. Same explicit re-render the segment-list dots do.
        this.render();
      });
      row.appendChild(text);
      row.appendChild(cb);
      list.appendChild(row);
    });
    section.appendChild(list);
    this.root.appendChild(section);
  }

  /** Render the connections section for a construction line.
   *  Three visual states:
   *
   *  - **collapsed empty** — no connections set and the user
   *    hasn't expanded the section. Single "Connections" toggle
   *    button.
   *  - **expanded empty** — the user clicked the toggle but no
   *    connections exist yet. Shows an empty-state hint.
   *  - **populated** — at least one connection applies. Shows
   *    one row per connection with its icon and plain-language
   *    label. */
  private appendConstructionConstraintsSection(line: ConstructionLine) {
    // Connection rows (issue #180 unification) — driven by the
    // Connections registry. Each registered kind (snap, offset,
    // mirror) contributes rows for this line; we render them in
    // one loop so adding a fourth kind later is one new entry in
    // the registry, not three more loops here.
    const allLines = projectConstructionLines(this.ctx.history.state);
    const project = this.ctx.history.state;
    const brokenIds = findBrokenCoincidentConstraints(
      project,
      allLines,
      this.ctx.scene.getPaperScope(),
    );
    const rowSpecs: ConnectionRowSpec[] = [];
    for (const conn of CONNECTIONS) {
      rowSpecs.push(
        ...conn.rowsForLine(line, project, { allLines, brokenIds }),
      );
    }
    const provider = this.constraintPickerProvider;
    const pickerState = provider?.state() ?? null;
    const pickerForThisLine =
      pickerState && pickerState.originLineId === line.id ? pickerState : null;
    // No rows from any connection kind AND no active picker —
    // render nothing. Empty sections are hidden per #131; the
    // picker case is the one remaining trigger for an empty-but-
    // visible section.
    if (rowSpecs.length === 0 && !pickerForThisLine) {
      return;
    }
    const section = panelSection("Connections");
    if (pickerForThisLine) {
      const required = requiredRefsForConstraint(pickerForThisLine.kind);
      const remaining = Math.max(0, required - pickerForThisLine.refs.length);
      const status = document.createElement("div");
      status.className = "constraints-pick-status";
      const text = document.createElement("p");
      text.className = "constraints-pick-status-text";
      const icon = constraintIcon(pickerForThisLine.kind);
      // The user picked a kind, then needs to point at N targets
      // on the canvas. Phrase it as "tap" since iPad is the primary
      // surface; mouse users read it the same way. Use "point" when
      // the kind only accepts anchor refs (e.g. coincident) so the
      // copy matches the picker's actual rule.
      const allowedKinds = allowedRefKindsForConstraint(pickerForThisLine.kind);
      const targetSingular =
        allowedKinds.length === 1 && allowedKinds[0] === "anchor"
          ? "point"
          : "feature";
      const targetPlural = `${targetSingular}s`;
      text.textContent =
        remaining === 0
          ? `Adding ${icon.label}…`
          : `Tap ${remaining} more ${
              remaining === 1 ? targetSingular : targetPlural
            } on the canvas to add ${icon.label}.`;
      status.appendChild(text);
      const cancelBtn = document.createElement("button");
      cancelBtn.type = "button";
      cancelBtn.className = "btn constraints-pick-cancel";
      cancelBtn.textContent = "Cancel";
      cancelBtn.addEventListener("click", () => provider?.cancel());
      status.appendChild(cancelBtn);
      section.appendChild(status);
    } else if (rowSpecs.length === 0) {
      const hint = document.createElement("p");
      hint.className = "constraints-empty-hint";
      hint.textContent = "No connections yet.";
      section.appendChild(hint);
    }
    if (rowSpecs.length > 0) {
      const list = document.createElement("ul");
      list.className = "constraints-list";
      for (const spec of rowSpecs) {
        list.appendChild(this.buildConnectionRow(spec));
      }
      section.appendChild(list);
    }
    this.root.appendChild(section);
  }

  /** Map an anchor's per-side handle state to the three indicator
   *  glyphs:
   *   - "none":   the requested side's handle is zero
   *   - "free":   the side has a non-zero handle, anchor not
   *                smooth-locked
   *   - "smooth": the side has a non-zero handle and the anchor
   *                is smooth-locked (both sides mirrored) */
  private classifyAnchorState(
    seg: paper.Segment,
    mode: "angle" | "smooth" | null,
    side: "in" | "out",
  ): "none" | "free" | "smooth" {
    const handle = side === "in" ? seg.handleIn : seg.handleOut;
    if (handle.isZero()) return "none";
    return mode === "smooth" ? "smooth" : "free";
  }

  /** Build one clickable end-indicator. The visible glyph is a
   *  small dot when this side has no bezier handle, a hollow circle
   *  when the side is curved-but-not-smooth, and a filled circle
   *  when smooth-locked. Click behavior depends on the active
   *  Construction edit-action: in delete mode it zeroes that side's
   *  handle (deleteHandle); otherwise it cycles the anchor through
   *  the 4-state bezier cycle, same as the canvas smooth-toggle. */
  private makeAnchorIndicator(
    constructionId: string,
    pathIndex: number,
    segmentIndex: number,
    side: "in" | "out",
    state: "none" | "free" | "smooth",
    /** Curve index of the row this dot belongs to. Used on
     *  mouseleave to re-arm the segment marching-ants overlay
     *  the row's own mouseenter set before the dot took over. */
    rowCurveIndex: number,
  ): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `segment-dot segment-dot-${state}`;
    const isDelete = this.effectiveEditAction() === "delete";
    btn.title = isDelete
      ? state === "none"
        ? "No bezier handle to delete"
        : "Click to delete this side's bezier handle"
      : state === "none"
        ? "No bezier handle — click to add"
        : state === "smooth"
          ? "Smooth-locked — click to cycle"
          : "Curved — click to cycle";
    btn.innerHTML =
      state === "none"
        ? // Tiny dot — the same y-position as the curved/smooth
          // glyphs so a stack of mixed-state indicators reads as a
          // continuous chain. The 24x24 button itself is the hit
          // target; the SVG just sets the visual.
          ANCHOR_CORNER_ICON
        : state === "smooth"
          ? ANCHOR_SMOOTH_ICON
          : ANCHOR_CURVED_ICON;
    // Hovering a dot switches the canvas highlight from the
    // segment marching-ants (set by the row's own mouseenter) to
    // a yellow ring on this anchor + the matching tangent line
    // and tip — the dot represents one specific side of one
    // anchor, so the canvas should show that thing rather than
    // the whole curve.
    btn.addEventListener("mouseenter", () => {
      this.ctx.scene.setConstructionSegmentHover(null);
      this.ctx.scene.setConstructionPanelHover({
        kind: "handle",
        constructionId,
        pathIndex,
        segmentIndex,
        side,
      });
    });
    btn.addEventListener("mouseleave", () => {
      this.ctx.scene.setConstructionPanelHover(null);
      // The cursor is still inside the row, but the row's own
      // mouseenter already fired earlier and won't re-fire — so
      // restore the segment marching-ants explicitly. The
      // segmentIndex passed here is the curve index for the row,
      // which is `aIdx` (the segment whose `out` handle this dot
      // bookends, OR for the end dot, the curve that ENDS at
      // `bIdx` — i.e., still curveIndex `aIdx`).
      this.ctx.scene.setConstructionSegmentHover({
        constructionId,
        pathIndex,
        segmentIndex: rowCurveIndex,
      });
    });
    btn.addEventListener("click", () => {
      if (this.effectiveEditAction() === "delete") {
        // Don't delete the segment — the user explicitly asked
        // for "do not delete the segments" from this affordance.
        // Only delete the side's handle. No-op when there's no
        // handle to delete.
        if (state === "none") return;
        this.deleteConstructionAnchorHandle(
          constructionId,
          pathIndex,
          segmentIndex,
          side,
        );
        return;
      }
      this.cycleConstructionAnchor(constructionId, pathIndex, segmentIndex);
    });
    return btn;
  }

  /** Zero one side of the targeted anchor's bezier handles. Used
   *  by the segment-list dot click while the user is in the
   *  delete sub-sub-tool. Path topology is preserved (same number
   *  of segments); the anchor's lock entry is dropped because
   *  asymmetric handles can't satisfy any lock. Drops the
   *  parametric primitive (destructive — no round-trip restore)
   *  via `dropConstructionPrimitive`. */
  private deleteConstructionAnchorHandle(
    constructionId: string,
    pathIndex: number,
    segmentIndex: number,
    side: "in" | "out",
  ) {
    const proj = this.ctx.history.state;
    const line = projectConstructionLines(proj).find(
      (l) => l.id === constructionId,
    );
    if (!line) return;
    const scope = this.ctx.scene.getPaperScope();
    const list = parseSubpathToSegmentList(line.pathData, pathIndex, scope);
    if (!list) return;
    const results = pathDeleteHandle(list, segmentIndex, side);
    if (results.length === 0) return;
    const result = results[0]!;
    const newPathData = segmentListToPathData(result, scope);
    const filteredAnchors = (line.smoothAnchors ?? []).filter(
      (a) => !(a.pathIndex === pathIndex && a.segmentIndex === segmentIndex),
    );
    const nextSmoothAnchors =
      filteredAnchors.length > 0 ? filteredAnchors : undefined;
    const newKind: "polyline" | "polygon" = result.closed
      ? "polygon"
      : "polyline";
    this.ctx.commit("Delete bezier handle", (proj) => ({
      ...proj,
      constructionLines: (proj.constructionLines ?? []).map((l) =>
        l.id === constructionId
          ? dropConstructionPrimitive(
              l,
              proj,
              {
                pathData: newPathData,
                smoothAnchors: nextSmoothAnchors,
                kind: newKind,
              },
              { preserveOrigin: false },
            )
          : l,
      ),
    }));
    this.render();
  }

  /** Apply one bezier-cycle step to the targeted anchor. Mirrors the
   *  Construction tool's `toggleSmoothLock` exactly: the same
   *  geometry + smoothAnchors update goes through the same
   *  history-aware commit that drops parametric primitive metadata
   *  on edit. Re-uses `cycleAnchorBezier` so both call sites stay in
   *  lock-step. */
  private cycleConstructionAnchor(
    constructionId: string,
    pathIndex: number,
    segmentIndex: number,
  ) {
    const proj = this.ctx.history.state;
    const line = projectConstructionLines(proj).find(
      (l) => l.id === constructionId,
    );
    if (!line) return;
    const result = cycleAnchorBezier(
      line,
      pathIndex,
      segmentIndex,
      this.ctx.scene.getPaperScope(),
    );
    if (!result) return;
    const scope = this.ctx.scene.getPaperScope();
    this.ctx.commit(result.label, (proj) => ({
      ...proj,
      constructionLines: (proj.constructionLines ?? []).map((l) =>
        l.id === constructionId
          ? dropConstructionPrimitive(
              l,
              proj,
              {
                pathData: result.pathData,
                smoothAnchors: result.smoothAnchors,
                bezierMemory: result.bezierMemory,
              },
              { preserveOrigin: true, scope },
            )
          : l,
      ),
    }));
    // The subscribed `history.subscribe` re-render skips when an
    // input inside the panel is focused — and clicking a dot button
    // moves focus to that button, which would otherwise leave the
    // indicators showing the pre-click state. Explicitly re-render
    // here so the icons + bridges reflect the new state immediately.
    this.render();
  }
}
